import React, { useCallback, useEffect, useMemo } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import useMerchandiseOffer from 'hooks/Merchandise/useMerchandiseOffer'
import { getMerchandiseCategorySearchUrl, getMerchandiseURL } from 'components/Merchandise/merchandiseUtils'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import PriceRowMerchandisePriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandisePriceStack'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import MerchandiseCartReviewTileContent from 'components/Pages/CartReviewPage/MerchandiseCartReviewTileContent'
import MerchandiseCartReviewTileActions from 'components/Pages/CartReviewPage/MerchandiseCartReviewTileActions'
import { generateMerchandiseCartItem } from 'lib/checkout/merchandiseCartItemUtils'
import MerchandiseCartReviewCondensedTileContent from 'components/Pages/CartReviewPage/MerchandiseCartReviewCondensedTileContent'
import MerchandiseCartReviewTileQuantitySelector from 'components/Pages/CartReviewPage/MerchandiseCartReviewTileQuantitySelector'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import { generateLoyaltyPointsCalculatorMerchandiseOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import styled from 'styled-components'
import { rem } from 'polished'

const MerchandiseCondensedTileWrapper = styled.div`
  max-width: ${rem(500)};
  width: 100%;
  
  /* Target the Element inside the horizontal Group to make it grow and push delete button right */
  > * > * > * > div:first-child {
    flex-grow: 1;
  }
`

interface Props {
  /**
   * The multi-cart item containing the merchandise item
   */
  multiCartItems: Array<App.MultiCartMerchandiseItem>
  /**
   * The 'primary' item being used for display on this tile
   */
  primaryItem: App.Checkout.MerchandiseItem
  /**
   * All the items represented by this tile
   *
   * They should all be the same offer id/variant id
   * (this is how we determine 'quantity' of an item)
   */
  items: Array<App.Checkout.MerchandiseItem>
  tileVariant?: 'default' | 'condensed'
}

function MerchandiseMultiCartReviewTile(props: Props) {
  const { multiCartItems, primaryItem, items, tileVariant = 'default' } = props

  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()

  // Extract state directly from multiCartItem
  const deleted = multiCartItems.every(multiCartItem => multiCartItem.state === 'deleted')
  const saveForLater = multiCartItems.every(multiCartItem => multiCartItem.state === 'save_for_later')
  const [merchandise, fetching, error] = useMerchandiseOffer(primaryItem.advertId)
  const [category] = useMerchandiseCategory({ id: merchandise?.category?.id })

  const variant = useMemo(() => {
    return merchandise?.variants.find(variant => variant.id === primaryItem.variantId)
  }, [merchandise, primaryItem.variantId])

  const onDeleteCartItem = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    const currentStates = multiCartItems.reduce((acc, multiCartItem) => {
      acc[multiCartItem.id] = multiCartItem.state
      return acc
    }, {} as Record<string, App.MultiCartItemState>)

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: multiCartItems.map(multiCartItem => multiCartItem.id),
      currentStates,
    })
  }

  const onSaveCartItemForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const currentSavedForLater = multiCartItems.every(multiCartItem => multiCartItem.state === 'save_for_later')

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: multiCartItems.map(multiCartItem => multiCartItem.id),
      saveForLater: !currentSavedForLater,
    })
  }

  const onAddItem = useCallback(() => {
    if (merchandise && variant && activeCartId) {
      const newItem = generateMerchandiseCartItem(merchandise, variant)

      // For multi-cart, we need to add the item to the active cart
      multiCartActorRef.send({
        type: 'add items to active cart',
        items: [newItem],
        skipNotification: true,
      })
    }
  }, [merchandise, variant, activeCartId, multiCartActorRef])

  const onRemoveItem = () => {
    // find last item in multiCartItems
    if (!activeCartId) return
    if (multiCartItems.length === 0) return

    const lastItem = multiCartItems[multiCartItems.length - 1]

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [lastItem.id],
      currentStates: {
        [lastItem.id]: lastItem.state,
      },
    })
  }

  // No UI yet for 'unavailable' situations, will add later
  // const unavailable = variant && variant?.inventory < items.length
  const soldOut = variant && !variant?.inventory

  const pointsConfig = useMemo<LoyaltyPointsEarningConfiguration | undefined>(() => {
    if (!soldOut && variant?.price) {
      return {
        calculationRequests: [generateLoyaltyPointsCalculatorMerchandiseOptions(variant, {
          price: variant.price * items.length,
        })],
        calculationType: 'estimate',
      }
    }
  }, [soldOut, variant, items.length])

  useEffect(() => {
    if (variant && soldOut && !saveForLater) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: multiCartItems.map(multiCartItem => multiCartItem.id),
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [variant, soldOut, saveForLater, activeCartId, multiCartItems, multiCartReviewActorRef])

  if (fetching) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (error || !merchandise || !variant) {
    return <MessageBanner kind="critical" density="compact" icon={<LineInfoCircleIcon />}>
      Your item is not available for purchase at the moment. Please try again later.
    </MessageBanner>
  }

  if (deleted) {
    return <UndoDeletedItemsTile
      title={merchandise.name}
      onUndoClick={() => {
        multiCartActorRef.send({
          type: 'undo delete',
          itemIds: multiCartItems.map(multiCartItem => multiCartItem.id),
        })
      }}
    />
  }

  const image = variant?.images[0]
  const merchandiseUrl = getMerchandiseURL(merchandise)
  const similarItemsUrl = category ? getMerchandiseCategorySearchUrl(category) : undefined

  if (tileVariant === 'condensed') {
    return <MerchandiseCondensedTileWrapper>
      <CondensedCartReviewTile
        to={merchandiseUrl}
        content={<MerchandiseCartReviewCondensedTileContent
          brand={merchandise.brand.name}
          title={merchandise.name}
          variant={variant}
        />}
        banner={soldOut && <MessageBanner
          kind="warning"
          density="compact"
          icon={<LineInfoCircleIcon />}
        >
          Item is out of stock. <TextLink to={similarItemsUrl} target="_blank">View similar items</TextLink>
        </MessageBanner>}
        imageElement={image ? <CartReviewTileImageContent image={image} objectFit="contain" applyImagePadding /> : undefined}
        price={<PriceRowMerchandisePriceStack
          direction="horizontal"
          size="S"
          variant={variant}
          quantity={items.length}
        />}
        onDelete={onDeleteCartItem}
        quantity={<MerchandiseCartReviewTileQuantitySelector
          quantity={items.length}
          inventory={variant.inventory}
          onAddItem={onAddItem}
          onRemoveItem={onRemoveItem}
        />}
      />
    </MerchandiseCondensedTileWrapper>
  }

  return <CartReviewTile
    to={merchandiseUrl}
    isBookable={!!variant.inventory}
    saveForLater={saveForLater}
    imageElement={image ? <CartReviewTileImageContent image={image} objectFit="contain" applyImagePadding /> : undefined}
    content={<MerchandiseCartReviewTileContent
      brand={merchandise.brand.name}
      title={merchandise.name}
      variant={variant}
      loyaltyPointsEarningConfiguration={pointsConfig}
      label={<MultiCartParticipantLabel item={multiCartItems[0]} />}
    />}
    banner={soldOut && <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
      Item is out of stock. <TextLink to={similarItemsUrl} target="_blank">View similar items</TextLink>
    </MessageBanner>}
    price={<PriceRowMerchandisePriceStack
      direction="horizontal"
      size="S"
      variant={variant}
      quantity={items.length}
    />}
    actions={<MerchandiseCartReviewTileActions
      variant={variant}
      itemCount={items.length}
      saveForLater={saveForLater}
      onSaveForLater={onSaveCartItemForLater}
      onDelete={onDeleteCartItem}
      onAddItem={onAddItem}
      onRemoveItem={onRemoveItem}
    />}
  />
}

export default MerchandiseMultiCartReviewTile
